/**
 * Router state management using Preact Signals
 *
 * This state store manages navigation, route history, and URL synchronization
 * following the same patterns as other state stores in the application.
 */

import type { GuideOpenMode, GuideOpenOptions, GuideStack, GuideTarget } from '@elara/client-core/states/guide-stack';
import type { Route, RouteGuard } from '@elara/client-core/states/routing-contract';
import type { PreviewLinkTarget } from '@elara/domain/schemas/preview-schemas';

import type { AppSettings } from '#AppSettings.js';

import { ROUTES } from '@elara/client-core/constants';
import {
  buildHashFragment,
  buildPathWithParams as buildPath,
  buildUrlFromRoute,
  buildUrlWithParams,
  extractParams as extractRouteParams,
  matchPath,
  parseHashFragment,
} from '@elara/client-core/lib/route-utils';
import { createState } from '@elara/client-core/lib/signals-utils';
import {
  currentGuide,
  moveInStack,
  newGuideKey,
  openInStack,
  parseGuideStack,
  withCurrentTitle,
} from '@elara/client-core/states/guide-stack';
import { parsePreviewHref } from '@elara/domain/schemas/preview-schemas';

import { DEFAULT_TITLE, registerDefaultRoutes } from '#routes.js';

// `Route` and `RouteGuard` are part of the platform-neutral routing contract (DOM-free, shared with
// `@elara/mobile`). Re-exported here so existing `#states/RouterState.js` import paths keep working.
export type { Route, RouteGuard } from '@elara/client-core/states/routing-contract';

export interface RouteConfig {
  path: string;
  component: React.ComponentType<Record<string, unknown>>;
  requiresAuth?: boolean;
  /**
   * Render this route on its own, outside every signed-in gate (auth redirects, the membership
   * payment and onboarding gates, the policy dialog) and without waiting for the profile. For public
   * pages that signed-out visitors AND signed-in non-owners must both see as-is, like a shared itinerary.
   */
  standalone?: boolean;
  title?: string | ((params: Record<string, string>) => string);
}

/**
 * Synthetic base origin used only to parse route strings into their pathname / query / hash.
 *
 * `parseRoute` reads nothing but those three components — never the origin — so the base used to
 * resolve a relative path is irrelevant to the result. Historically this was
 * `window.location.origin`, which coupled route parsing to the live page origin (and the DOM) and
 * blocked reuse off the web. A fixed, never-resolvable base (RFC 6761 `.invalid` TLD) makes parsing
 * deterministic and origin-independent — the deep-link foundation for ELARA-699. (Native has its own
 * DOM-free parser in `@elara/mobile`'s route table.)
 */
const ROUTE_PARSE_BASE = 'https://elara.invalid';

/** What a browser history entry carries for the guide panel: the stack it was showing. */
interface GuideHistoryState {
  guide: GuideStack;
}

/** The preview a route's hash names, if any. */
function previewOf(route: Route | undefined): PreviewLinkTarget | undefined {
  return route?.hash ? parsePreviewHref(route.hash) : undefined;
}

/** Whether `route` shows the current entry of `stack`. */
function showsCurrentGuide(route: Route | undefined, stack: GuideStack | undefined): stack is GuideStack {
  const target = previewOf(route);
  const entry = currentGuide(stack);
  return !!target && !!entry && target.type === entry.type && target.id === entry.id;
}

export function createRouterState(settings: AppSettings) {
  const state = createState({
    /** Current route information */
    currentRoute: undefined as Route | undefined,
    /** Current query parameters (reactive) */
    queryParams: {} as Record<string, string>,
    /** Route configuration */
    routes: new Map<string, RouteConfig>(),
    /** Route guards for navigation protection */
    guards: [] as RouteGuard[],
    /** Navigation state */
    isNavigating: false,
    /** Previous route for back navigation */
    previousRoute: undefined as Route | undefined,
    /**
     * The guide panel's stack (NavigationPort contract). Each browser history entry also saves the
     * stack it showed in `history.state`, which is what browser Back/Forward and a reload restore;
     * this copy additionally holds what the browser cannot hand back — titles captured after an
     * entry was written. See {@link reconcileGuideStack}.
     */
    guideStack: undefined as GuideStack | undefined,

    get isInAdmin(): boolean {
      if (!this.currentRoute?.path) return false;
      return !!this.matchPath(this.currentRoute.path, ROUTES.ADMIN);
    },

    /** The entity-preview sheet currently open — derived from the route's `#preview=…` hash (web). */
    get previewTarget(): PreviewLinkTarget | undefined {
      return this.currentRoute?.hash ? parsePreviewHref(this.currentRoute.hash) : undefined;
    },

    /** Navigate to a new route */
    navigate(path: string, options?: { replace?: boolean; state?: unknown }) {
      this.isNavigating = true;

      try {
        const route = this.parseRoute(path);

        // Check route guards before navigation
        if (!this.canNavigateTo(route)) {
          console.debug(`[RouterState] Navigation blocked by route guards to: ${path}`);
          return;
        }

        // Call onLeave callbacks for all guards (after canLeave has passed)
        if (this.currentRoute) {
          for (const guard of this.guards) {
            guard.onLeave?.(this.currentRoute, route);
          }
        }

        if (!options?.replace) {
          this.previousRoute = this.currentRoute;
        }

        this.currentRoute = route;
        this.queryParams = route.query;
        this.updateBrowserHistory(route, options);
        this.reconcileGuideStack(route);
        this.updatePageTitle(route);
      } finally {
        this.isNavigating = false;
      }
    },

    /** Build a path with parameters */
    buildPathWithParams(path: string, params: Record<string, string>): string {
      return buildPath(path, params);
    },

    /** Go back in history */
    goBack() {
      if (window.history.length > 1) {
        window.history.back();
      }
    },

    /**
     * Whether back navigation is possible (NavigationPort contract).
     * Web-backed by the browser history length; the native impl maps this to
     * React Navigation's `canGoBack()`. A method, not a getter: getters become
     * cached computed signals under `createState`, which would freeze this to its
     * first read of the (non-signal) `window.history`.
     */
    canGoBack(): boolean {
      return typeof window !== 'undefined' && window.history.length > 1;
    },

    /**
     * Confirm leaving the current route (NavigationPort contract). Web-backed by
     * `window.confirm`; the native impl substitutes a platform dialog. Returning
     * `true` outside a browser keeps navigation unblocked in non-DOM contexts.
     */
    confirmLeave(message: string): boolean {
      if (typeof window === 'undefined') return true;
      return window.confirm(message);
    },

    /** Parse a route string into Route object */
    parseRoute(path: string): Route {
      try {
        const url = new URL(path, ROUTE_PARSE_BASE);
        const params = this.extractParams(url.pathname);
        const query = Object.fromEntries(url.searchParams.entries());

        return { path: url.pathname, params, query, hash: url.hash || undefined };
      } catch (error) {
        // If URL parsing fails, log the error and return a route that will trigger 404
        console.error('[RouterState] Failed to parse route:', {
          path,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });

        // Return a route with an invalid path that will be caught by the 404 handler
        // This ensures users see an error page instead of being silently redirected
        return {
          path: path.startsWith('/') ? path : `/${path}`, // Ensure it starts with /
          params: {},
          query: {},
          hash: undefined,
        };
      }
    },

    /** Extract route parameters from path */
    extractParams(path: string): Record<string, string> {
      const routePatterns = [...this.routes.keys()];
      return extractRouteParams(path, routePatterns);
    },

    /** Match a path against a route pattern */
    matchPath(path: string, pattern: string): { params: Record<string, string> } | null {
      return matchPath(path, pattern);
    },

    /** Check if a path matches a route pattern (public API) */
    doesPathMatch(path: string, pattern: string): boolean {
      return matchPath(path, pattern) !== null;
    },

    /**
     * Update browser history. An entry whose URL still shows the open guide carries its stack, so
     * browser Back/Forward land with the panel's history intact; before a push, the entry being left
     * is saved with the latest stack (titles included).
     */
    updateBrowserHistory(route: Route, options?: { replace?: boolean }) {
      const url = this.buildUrl(route);
      const state: GuideHistoryState | null = showsCurrentGuide(route, this.guideStack)
        ? { guide: this.guideStack }
        : null;
      if (options?.replace) {
        window.history.replaceState(state, '', url);
      } else {
        this.saveGuideToCurrentEntry();
        window.history.pushState(state, '', url);
      }
    },

    /**
     * Save `stack` into the browser's current history entry, when that entry shows the stack's current
     * guide. How titles and the latest forward entries reach browser history. Reads the entry from
     * `window.location`, not {@link currentRoute}: callers may already have moved `currentRoute` on to
     * the route they are about to push, and rewriting the entry being left with that URL would corrupt
     * history.
     */
    saveGuideToCurrentEntry(stack?: GuideStack) {
      if (typeof window === 'undefined') return;
      const toSave = stack ?? this.guideStack;
      const { pathname, search, hash, href } = window.location;
      if (!showsCurrentGuide(this.parseRoute(pathname + search + hash), toSave)) return;
      const state: GuideHistoryState = { guide: toSave };
      window.history.replaceState(state, '', href);
    },

    /**
     * Bring {@link guideStack} in line with a route the browser (or a non-guide navigation) landed on,
     * given the stack that history entry saved, if any:
     *
     * - no guide in the route → no stack;
     * - the saved stack shows this guide → adopt it, keeping this session's copy when it is the same
     *   stack (its titles are newer than the saved ones), at the saved position;
     * - this session's stack already shows this guide → keep it (a panel switch under an open guide);
     * - otherwise (a shared link, a hand-edited hash, an entry from before this feature) → a new
     *   one-guide stack, saved so Back and Forward return to it.
     */
    reconcileGuideStack(route: Route, savedState?: unknown) {
      const target = previewOf(route);
      if (!target) {
        this.guideStack = undefined;
        return;
      }
      const saved = parseGuideStack((savedState as Partial<GuideHistoryState> | null | undefined)?.guide);
      const memory = this.guideStack;
      if (saved && showsCurrentGuide(route, saved)) {
        const sameStack =
          memory?.key === saved.key && memory.entries[saved.index]?.key === saved.entries[saved.index]?.key;
        this.guideStack = sameStack ? moveInStack(memory, saved.index) : saved;
        if (this.guideStack !== saved) this.saveGuideToCurrentEntry();
        return;
      }
      if (showsCurrentGuide(route, memory)) return;
      this.guideStack = openInStack(undefined, target, 'reset', newGuideKey);
      this.saveGuideToCurrentEntry();
    },

    /**
     * Open a guide (NavigationPort contract). A push saves the entry being left with the new stack, so
     * Forward from it leads back here; a replace (a redirect) rewrites the current entry instead. A
     * reset with a `parent` pushes the parent's own history entry first, so browser Back and the
     * breadcrumb both return to it.
     */
    openGuide(target: GuideTarget, mode: GuideOpenMode, options: GuideOpenOptions = {}) {
      const fromRoute = this.currentRoute;
      if (!fromRoute) return;
      const previous = this.guideStack;
      const parent = mode === 'reset' ? options.parent : undefined;
      const next = openInStack(previous, target, mode, newGuideKey, { parent });
      if (next === previous) return;

      const hashParams = this.getHashParams();
      delete hashParams.preview;
      delete hashParams.id;
      for (const key of Object.keys(currentGuide(previous)?.view ?? {})) delete hashParams[key];
      // `preview` leads: `parsePreviewHref` only reads a hash that starts with it.
      const routeFor = (guide: GuideTarget): Route => {
        const hash = buildHashFragment({ preview: guide.type, id: guide.id, ...guide.view, ...hashParams });
        return { ...fromRoute, hash: hash || undefined };
      };
      const route = routeFor(target);

      const replacing = mode === 'replace' && previous?.key === next.key;
      if (!replacing && previous) {
        this.saveGuideToCurrentEntry(previous.key === next.key ? { ...next, index: previous.index } : previous);
      }
      this.guideStack = next;
      this.previousRoute = fromRoute;
      this.currentRoute = route;
      const state: GuideHistoryState = { guide: next };
      if (replacing) {
        window.history.replaceState(state, '', this.buildUrl(route));
      } else {
        if (parent) {
          const parentState: GuideHistoryState = { guide: { ...next, index: 0 } };
          window.history.pushState(parentState, '', this.buildUrl(routeFor(parent)));
        }
        window.history.pushState(state, '', this.buildUrl(route));
      }
    },

    /**
     * Move within the open stack (NavigationPort contract) — through browser history, so the
     * browser's own Back/Forward, keyboard shortcuts and mouse buttons stay the same navigation.
     */
    goToGuide(index: number) {
      const stack = this.guideStack;
      if (!stack || index < 0 || index >= stack.entries.length || index === stack.index) return;
      window.history.go(index - stack.index);
    },

    /** Close the guide (NavigationPort contract). Browser Back afterwards reopens it, stack intact. */
    closeGuide() {
      const fromRoute = this.currentRoute;
      if (!fromRoute) return;
      const hashParams = this.getHashParams();
      delete hashParams.preview;
      delete hashParams.id;
      for (const key of Object.keys(currentGuide(this.guideStack)?.view ?? {})) delete hashParams[key];
      const hash = buildHashFragment(hashParams) || undefined;

      this.saveGuideToCurrentEntry();
      this.guideStack = undefined;
      if (hash === fromRoute.hash) return;
      const route = { ...fromRoute, hash };
      this.previousRoute = fromRoute;
      this.currentRoute = route;
      window.history.pushState(null, '', this.buildUrl(route));
    },

    /**
     * Record the open guide's title (NavigationPort contract). Guides report it while rendering, so
     * saving it into browser history waits for a microtask rather than writing history mid-render.
     */
    setGuideTitle(title: string) {
      const stack = this.guideStack;
      if (!stack) return;
      const next = withCurrentTitle(stack, title);
      if (next === stack) return;
      this.guideStack = next;
      queueMicrotask(() => {
        if (this.guideStack === next) this.saveGuideToCurrentEntry();
      });
    },

    /** Update page title based on current route */
    updatePageTitle(route: Route) {
      if (typeof window === 'undefined') return;

      const routeConfig = this.getCurrentRouteConfig();
      let title: string;

      if (routeConfig?.title) {
        if (typeof routeConfig.title === 'function') {
          title = routeConfig.title(route.params);
        } else {
          title = routeConfig.title;
        }
      } else {
        // Fallback to default title if no route-specific title is defined
        title = DEFAULT_TITLE;
      }

      document.title = title;
    },

    /** Update page title directly (for dynamic updates from components) */
    setPageTitle(title: string) {
      if (typeof window === 'undefined') return;
      document.title = title;
    },

    /** Build URL from route */
    buildUrl(route: Route): string {
      return buildUrlFromRoute(route);
    },

    /**
     * Build a URL string with modified hash parameters, preserving existing query params and hash fragments.
     * Useful for creating links that update specific hash params without losing other URL state.
     *
     * @param hashParams - Hash parameters to add or update (merged with existing hash params)
     * @param queryParams - Optional query parameters to add or update (merged with existing query params)
     * @returns Built URL string, or empty string if no current route
     *
     * @example
     * // Add preview params while preserving existing hash and query params
     * routerState.buildUrlWithHashParams({ preview: 'destination', id: '123' })
     * // => '/trip/456?tab=hotels#filter=active&preview=destination&id=123'
     */
    buildUrlWithHashParams(hashParams: Record<string, string>, queryParams?: Record<string, string>): string {
      if (!this.currentRoute) return '';
      return buildUrlWithParams(this.currentRoute, hashParams, queryParams);
    },

    /** Register route configuration */
    registerRoute(config: RouteConfig) {
      this.routes.set(config.path, config);

      // If we have a current route, check if it matches this new pattern
      // and update the parameters if needed
      if (this.currentRoute) {
        const params = this.extractParams(this.currentRoute.path);
        if (Object.keys(params).length > 0) {
          this.currentRoute = { ...this.currentRoute, params };
        }
      }
    },

    /** Get current route config */
    getCurrentRouteConfig(): RouteConfig | undefined {
      if (!this.currentRoute) return undefined;

      // Find exact match first
      let config = this.routes.get(this.currentRoute.path);
      if (config) return config;

      // Find pattern match
      for (const [pattern, routeConfig] of this.routes.entries()) {
        if (this.matchPath(this.currentRoute.path, pattern)) {
          return routeConfig;
        }
      }

      return undefined;
    },

    /** Check if current route is not found (no matching route found) */
    isNotFound(): boolean {
      if (!this.currentRoute) return false;
      return this.getCurrentRouteConfig() === undefined;
    },

    /** Generate path from route pattern and params */
    generatePath(pattern: string, params: Record<string, string>): string {
      return buildPath(pattern, params);
    },

    /** Popstate handler reference for cleanup (instance-scoped) */
    handlePopState: undefined as ((event: PopStateEvent) => void) | undefined,

    /** Cleanup function reference to ensure idempotency (instance-scoped) */
    cleanupFunctionRef: undefined as (() => void) | undefined,

    /** Start router monitors - conforms to Monitorable interface */
    startMonitors() {
      // Return existing cleanup function if already started
      if (this.cleanupFunctionRef) {
        return this.cleanupFunctionRef;
      }

      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        return () => {}; // Return empty cleanup function
      }

      // Register default routes
      registerDefaultRoutes(this);

      // Set initial route
      const initialRoute = this.parseRoute(window.location.pathname + window.location.search + window.location.hash);
      this.currentRoute = initialRoute;
      this.queryParams = initialRoute.query;
      // A reload lands on an entry that may have saved a guide stack: restore it.
      this.reconcileGuideStack(initialRoute, window.history.state);
      this.updatePageTitle(initialRoute);

      // Listen to browser back/forward
      this.handlePopState = (event: PopStateEvent) => {
        const route = this.parseRoute(window.location.pathname + window.location.search + window.location.hash);
        this.currentRoute = route;
        this.queryParams = route.query;
        this.reconcileGuideStack(route, event.state);
        this.updatePageTitle(route);
      };

      window.addEventListener('popstate', this.handlePopState);

      // Store and return cleanup function reference
      this.cleanupFunctionRef = () => {
        if (this.handlePopState) {
          window.removeEventListener('popstate', this.handlePopState);
          this.handlePopState = undefined;
        }
        this.cleanupFunctionRef = undefined;
      };

      return this.cleanupFunctionRef;
    },

    /** Get history statistics */
    getHistoryStats() {
      return { currentSize: window.history.length, canGoBack: window.history.length > 1 };
    },

    /** Update query parameters without changing the path */
    updateQueryParams(newParams: Record<string, string>) {
      if (!this.currentRoute) return;

      const updatedRoute = { ...this.currentRoute, query: { ...this.currentRoute.query, ...newParams } };

      this.previousRoute = this.currentRoute;
      this.currentRoute = updatedRoute;
      this.queryParams = updatedRoute.query;
      this.updateBrowserHistory(updatedRoute);
    },

    /** Remove specific query parameters */
    removeQueryParams(paramNames: string[]) {
      if (!this.currentRoute) return;

      const updatedQuery = { ...this.currentRoute.query };
      for (const paramName of paramNames) {
        delete updatedQuery[paramName];
      }

      const updatedRoute = { ...this.currentRoute, query: updatedQuery };

      this.previousRoute = this.currentRoute;
      this.currentRoute = updatedRoute;
      this.queryParams = updatedQuery;
      this.updateBrowserHistory(updatedRoute);
    },

    /** Get a specific query parameter value */
    getQueryParam(name: string): string | undefined {
      return this.queryParams[name];
    },

    /** Get all hash parameters as key-value pairs */
    getHashParams(): Record<string, string> {
      if (!this.currentRoute?.hash) return {};
      return parseHashFragment(this.currentRoute.hash);
    },

    /** Get a specific hash parameter value */
    getHashParam(name: string): string | undefined {
      return this.getHashParams()[name];
    },

    /** Update hash fragment without changing path or query params */
    updateHashFragment(newParams: Record<string, string>) {
      if (!this.currentRoute) return;

      const currentHashParams = this.getHashParams();
      const updatedHashParams = { ...currentHashParams, ...newParams };
      const newHash = buildHashFragment(updatedHashParams);

      const updatedRoute = { ...this.currentRoute, hash: newHash || undefined };

      this.updateBrowserHistory(updatedRoute);
      this.previousRoute = this.currentRoute;
      this.currentRoute = updatedRoute;
      this.reconcileGuideStack(updatedRoute);
    },

    /** Remove specific hash parameters */
    removeHashParams(paramNames: string[]) {
      if (!this.currentRoute) return;

      const currentHashParams = this.getHashParams();
      const updatedHashParams = { ...currentHashParams };

      for (const paramName of paramNames) {
        delete updatedHashParams[paramName];
      }

      const newHash = buildHashFragment(updatedHashParams);
      const updatedRoute = { ...this.currentRoute, hash: newHash || undefined };

      this.updateBrowserHistory(updatedRoute);
      this.previousRoute = this.currentRoute;
      this.currentRoute = updatedRoute;
      this.reconcileGuideStack(updatedRoute);
    },

    /**
     * Check if navigation to a route is allowed by guards
     *
     * All guards are checked synchronously. If any guard returns false,
     * navigation is blocked and a message is logged.
     */
    canNavigateTo(targetRoute: Route): boolean {
      if (!this.currentRoute) return true;

      for (const guard of this.guards) {
        const canLeave = guard.canLeave(this.currentRoute, targetRoute);
        if (!canLeave) {
          console.log(`[RouterState] Navigation blocked by route guards to: ${targetRoute.path}`);
          return false;
        }
      }

      return true;
    },

    /** Add a route guard */
    addGuard(guard: RouteGuard) {
      this.guards.push(guard);
    },

    /** Remove a route guard */
    removeGuard(guard: RouteGuard) {
      const index = this.guards.indexOf(guard);
      if (index !== -1) {
        this.guards.splice(index, 1);
      }
    },

    /** Clear all route guards */
    clearGuards() {
      this.guards = [];
    },

    /** Stop router monitors - conforms to Monitorable interface */
    stopMonitors() {
      if (this.handlePopState) {
        window.removeEventListener('popstate', this.handlePopState);
        this.handlePopState = undefined;
      }
      this.cleanupFunctionRef = undefined;
      this.clearGuards();
    },
  });

  return state;
}

export type RouterState = ReturnType<typeof createRouterState>;
